import { useEffect } from 'react';
import { applyHreflang } from './useDocumentMeta';
import { SITE_LOCALE, SITE_ORIGIN, OG_LOCALE_TAGS, ogImageUrlForPath } from '@/lib/site-config';

interface DocumentHeadOptions {
  title: string;
  description: string;
  url?: string;
  type?: 'website' | 'article';
  image?: string;
  imageAlt?: string;
  publishedTime?: string;
  author?: string;
  section?: string;
  noindex?: boolean;
}

const DEFAULT_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;
const DEFAULT_IMAGE_ALT = 'LogPulse Dashboard showing AI-powered log analysis';
const SITE_NAME = 'LogPulse';

function setMetaTag(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property';
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

/**
 * Updates document head meta tags for SEO and social sharing.
 * Restores defaults on unmount.
 */
export function useDocumentHead(options: DocumentHeadOptions) {
  useEffect(() => {
    const {
      title,
      description,
      url,
      type = 'website',
      imageAlt = DEFAULT_IMAGE_ALT,
      publishedTime,
      author,
      section,
      noindex = false,
    } = options;

    const pathname = typeof window !== 'undefined' ? window.location.pathname : url ?? '/';
    // Default to the per-page OG card (built by scripts/generate-og-images.ts);
    // callers can still pass an explicit image. The 404 page opts out via
    // noindex — an unknown path has no generated card, so use the site default.
    const image = options.image ?? (noindex ? DEFAULT_IMAGE : ogImageUrlForPath(pathname));

    const fullTitle = title.includes('LogPulse') ? title : `${title}: LogPulse`;
    const fullUrl = url ? `${SITE_ORIGIN}${url}` : `${SITE_ORIGIN}${pathname === '/' ? '' : pathname}`;

    document.title = fullTitle;

    setMetaTag('title', fullTitle, true);
    setMetaTag('description', description, true);
    setMetaTag(
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      true,
    );

    setMetaTag('og:type', type);
    setMetaTag('og:url', fullUrl);
    setMetaTag('og:title', fullTitle);
    setMetaTag('og:description', description);
    setMetaTag('og:image', image);
    setMetaTag('og:image:alt', imageAlt);
    setMetaTag('og:site_name', SITE_NAME);
    setMetaTag('og:locale', OG_LOCALE_TAGS[SITE_LOCALE]);

    // Twitter/X cards use name= (not property=), matching the shell in index.html.
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:url', fullUrl, true);
    setMetaTag('twitter:title', fullTitle, true);
    setMetaTag('twitter:description', description, true);
    setMetaTag('twitter:image', image, true);

    setCanonical(fullUrl);
    applyHreflang(pathname);

    if (type === 'article') {
      if (publishedTime) setMetaTag('article:published_time', publishedTime);
      if (author) setMetaTag('article:author', author);
      if (section) setMetaTag('article:section', section);
    }

    return () => {
      const defaultTitle = 'LogPulse: AI-Powered Log Management & Observability';
      const defaultDesc =
        'Search, detect anomalies, integrate cloud sources, and build pipelines with AI-powered log analysis. The most affordable log management platform for modern engineering teams.';
      document.title = defaultTitle;
      setMetaTag('title', defaultTitle, true);
      setMetaTag('description', defaultDesc, true);
      setMetaTag('og:type', 'website');
      setMetaTag('og:url', SITE_ORIGIN);
      setMetaTag('og:title', defaultTitle);
      setMetaTag('og:description', defaultDesc);
      setMetaTag('og:image', DEFAULT_IMAGE);
      setMetaTag('og:image:alt', DEFAULT_IMAGE_ALT);
      setMetaTag('twitter:title', defaultTitle, true);
      setMetaTag('twitter:description', defaultDesc, true);
      setMetaTag('twitter:url', SITE_ORIGIN, true);
      setCanonical(SITE_ORIGIN);
    };
  }, [options.title, options.url]);
}
